import Papa from 'papaparse';
import { html, css, nothing, TemplateResult } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';

import { validate } from '../../../../helpers/provetron.js';
import { NSUpload } from '../../../../ns-elements/ns-upload.js';
import { createLogger } from '../../../../ns-services/logger.js';
import { LiveAnnouncer } from '../../../../helpers/live-announcer.js';

const logger = createLogger('ns-spreadsheet');

interface Message {
  type: 'success' | 'info' | 'warning' | 'error';
  text: string;
}

@customElement(NSSpreadsheet.is)
export default class NSSpreadsheet extends NSJobComponent {
  static get is() {
    return 'ns-spreadsheet';
  }

  @query('#fileUpload')
  protected _domFileUpload?: NSUpload;

  @query('#templateLink')
  protected _domTemplateLink?: HTMLAnchorElement;

  @state()
  protected _messages: Message[] = [];

  protected _columns: string[] = [];
  protected _dataValidationRules: { [key: string]: string[] } = {};

  @state()
  protected _canImport: boolean = false;

  @state()
  protected _countValid = 0;
  @state()
  protected _countImport = 0;
  @state()
  protected _countFailed = 0;

  @state()
  public importantMessage?: string;

  protected _announcer = new LiveAnnouncer(this);

  public announce(message: string) {
    this._announcer.announce(message);
  }

  static get schema(): JobSchema {
    return {
      name: '@ns/spreadsheet',
      description: 'Creating a spreadsheet to fulfil a job with multiple entities',
      version: '1.0.0',
      inputs: {},
      outcomes: {
        complete: {
          type: 'button',
          label: 'Complete',
        },
      },
    };
  }

  static styles = [
    ...super.styles,
    css`
      #fileUpload {
        margin-bottom: var(--wa-space-m);
      }

      #templateLink .pill {
        background: var(--primary-color);
        color: var(--ns-theme-white);
      }

      .messages {
        padding: 0;
        list-style: none;
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();

    const blob = new Blob([this._columns.join(',')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    this._domTemplateLink?.setAttribute('href', url);

    this._domFileUpload?.init(['.csv', 'text/csv', 'application/vnd.ms-excel'], 'Spreadsheet needs to be csv format.');
  }

  protected async _validate() {
    this._resetValidation();

    let reader: ReadableStreamDefaultReader<any>;
    try {
      reader = this._getInputFileStream();
    } catch (err) {
      this._handleInputFileStreamError(err);
      return false;
    }

    let rowIndex = 2;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      if (this._validateRow(rowIndex, value)) {
        this._countValid++;
      } else {
        this._countFailed++;
      }
      rowIndex++;
    }

    if (this._countFailed > 0) {
      this.addMessage(
        'warning',
        `${this._countValid} valid, ${this._countFailed} failed. Please fix the errors and try again.`,
      );
      return false;
    }

    this.addMessage('info', `${this._countValid} valid, ready to be imported.`);
    this._canImport = true;
    return true;
  }
  protected _validateRow(rowIndex: number, value: any) {
    const result = validate(this._dataValidationRules, value);
    if (result === true) return true;

    Object.keys(result).forEach((column) => {
      Object.keys(result[column]).forEach((key) => {
        this.addMessage('error', `Row ${rowIndex}: Column: ${column} - ${result[column][key]}`);
      });
    });

    return false;
  }

  protected _handleInputFileStreamError(err: unknown) {
    if ((err as Error).message) {
      this.addMessage('error', (err as Error).message);
    } else {
      this.addMessage('error', 'Something went wrong, please try again.');
      logger.error(err);
    }
  }

  protected _getInputFileStream() {
    const file = this._domFileUpload?.getFile();
    if (!file) {
      throw new Error('Please select a file to import.');
    }

    const stream = this._readFile(file);
    if (!stream) {
      throw new Error('Failed to read from file, please try again.');
    }

    return stream.getReader();
  }

  protected _readFile(file: File) {
    const { readable, writable } = new TransformStream();
    const writer = writable.getWriter();

    Papa.parse(file, {
      header: true,
      worker: true,
      skipEmptyLines: true,
      step: (result) => writer.write(result.data),
      complete: () => writer.close(),
      error: (err) => writer.abort(err),
    });

    return readable;
  }

  protected _resetValidation() {
    this._countFailed = 0;
    this._countValid = 0;
    this._canImport = false;

    this.clearMessages();
  }

  protected addMessage(type: Message['type'], text: string) {
    this._messages = [...this._messages, { type, text }];
    this.announce(text);
  }
  protected clearMessages() {
    this._messages = [];
  }

  protected _renderMessages(): TemplateResult | typeof nothing {
    if (this._messages.length === 0) return nothing;

    return html`
      <div class="job-panel">
        <h3>Import Information</h3>
        <ul class="messages">
          ${this._messages.map(
            (message) => html`
              <li class="message message--${message.type}">${message.text}</li>
            `,
          )}
        </ul>
      </div>
    `;
  }
}
